import { Box, IconButton } from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { AdviseCurrentSupplier, AdviseSupplierInvoice } from '../../../../types/advise';
import { TextSpan } from '../../../Atoms/Text';
import { ConfirmDialog } from '../ConfirmDialog';
import { useConfirmedRemoval } from '../useConfirmedRemoval';
import { SupplierInvoiceRow } from './SupplierInvoiceRow';
import { InvoiceUpload } from './InvoiceUpload';
import { SupplierMonthlyAmount } from './SupplierMonthlyAmount';
import { ManualEntryNote, manualEntryNote } from './ManualEntryNote';

export interface CurrentSupplierCardProps {
  supplier: AdviseCurrentSupplier;
  canEdit: boolean;
  // Null opens the supplier's own entry.
  onOpen: (invoiceId: string | null) => void;
  onRemoveSupplier: () => Promise<string | null>;
  onUploadInvoice: (file: File) => Promise<string | null>;
  onRemoveInvoice: (invoiceId: string) => Promise<string | null>;
}

/** One current supplier: its monthly amount, invoices, manual entry and the actions on them. */
export const CurrentSupplierCard = ({
  supplier,
  canEdit,
  onOpen,
  onRemoveSupplier,
  onUploadInvoice,
  onRemoveInvoice
}: CurrentSupplierCardProps) => {
  const supplierRemoval = useConfirmedRemoval<AdviseCurrentSupplier>(onRemoveSupplier);
  const invoiceRemoval = useConfirmedRemoval<AdviseSupplierInvoice>((invoice) => onRemoveInvoice(invoice.id));
  const hasInvoices = supplier.invoices.length > 0;
  const note = manualEntryNote(supplier);

  return (
    <Box className="flex flex-col gap-3 rounded-lg border border-grey-3 bg-white p-5">
      <Box className="flex items-center justify-between gap-3">
        <TextSpan
          size="sm14"
          weight="semibold"
          color="grey8">
          {supplier.name}
        </TextSpan>
        <Box className="flex items-center gap-2">
          <SupplierMonthlyAmount supplier={supplier} />
          {canEdit && (
            <IconButton
              size="small"
              aria-label={`Delete ${supplier.name}`}
              onClick={() => supplierRemoval.ask(supplier)}>
              <DeleteIcon
                fontSize="small"
                className="text-grey-4"
              />
            </IconButton>
          )}
        </Box>
      </Box>
      {supplier.invoices.map((invoice) => (
        <SupplierInvoiceRow
          key={invoice.id}
          invoice={invoice}
          canEdit={canEdit}
          onOpen={() => onOpen(invoice.id)}
          onRemove={() => invoiceRemoval.ask(invoice)}
        />
      ))}
      {note && (
        <ManualEntryNote
          note={note}
          canEdit={canEdit}
          onOpen={() => onOpen(null)}
        />
      )}
      {canEdit && (
        <InvoiceUpload
          inputId={`invoice-upload-${supplier.id}`}
          label={hasInvoices ? 'Upload another invoice' : 'Upload Invoice'}
          onUpload={onUploadInvoice}
        />
      )}
      <ConfirmDialog
        open={invoiceRemoval.open}
        title={`Remove ${invoiceRemoval.target?.file_name ?? ''}?`}
        message="This removes the invoice and its line items from this supplier."
        confirmLabel="Remove"
        refusal={invoiceRemoval.refusal}
        onConfirm={invoiceRemoval.confirm}
        onCancel={invoiceRemoval.cancel}
      />
      <ConfirmDialog
        open={supplierRemoval.open}
        title={`Delete ${supplier.name}?`}
        message="This deletes the supplier, and its invoices and entries will be removed."
        refusal={supplierRemoval.refusal}
        onConfirm={supplierRemoval.confirm}
        onCancel={supplierRemoval.cancel}
      />
    </Box>
  );
};
